import { IPagination } from "./pagination";
import { Status } from "./state";

export enum BillingStatus {
  Active = "active",
  Closed = "closed",
  New = "new",
}

export enum BillingApiErrors {
  NotFound = "notFound",
  Closed = "closed",
  CannotUpdate = "cannotUpdate",
  CannotUpdateExpectedWasteAmount = "cannotUpdateExpectedWasteAmount",
  CannotUpdateExpectedExpenses = "cannotUpdateExpectedExpenses",
  CannotUpdateRates = "cannotUpdateRates",
  AnOpenBillingExists = "anOpenBillingExists",
  CannotDelete = "cannotDelete",
  NoPermission = "noPermission",
  Default = "default",
}

export enum BillingType {
  // Volume = "volume",
  Weight = "weight",
  // Flat = "flat",
}

export const BILLING_TYPES = Object.values(BillingType);

export enum BillingPaymentScheduleType {
  Annual = "annual",
  Quarterly = "quarterly",
}

export enum BillingPaymentPeriod {
  Annual = "annual",
  Q1 = "q1",
  Q2 = "q2",
  Q3 = "q3",
  Q4 = "q4",
}

export enum BillingRefundReason {
  Overpayment = "overpayment",
  Deactivation = "deactivation",
}

export const BILLING_PAYMENT_SCHEDULE_TYPES = Object.values(BillingPaymentScheduleType);

export enum BillingBlockReason {
  OnlyOneActive = "onlyOneActive",
  NoAvailableYear = "noAvailableYear",
}

export enum BillingApiErrors {}

export interface IBillingExpenseApi {
  id?: number;
  real: boolean;
  name: string;
  value: number;
}

export interface IBillingWeightedApi {
  rate: string;
  flat_fee: string;
  suggested_flat_fee: string;
  suggested_rate: string;
  avg_public_waste_per_person: number;
  last_years_waste_sum: number;
  min_waste_per_household: number;
  median_waste_per_household: number;
}

export interface IBillingLocationIncreaseComparisonApi {
  negative_25: number;
  negative_20_to_25: number;
  negative_15_to_20: number;
  negative_10_to_15: number;
  negative_5_to_10: number;
  negative_0_to_5: number;
  positive_0_to_5: number;
  positive_5_to_10: number;
  positive_10_to_15: number;
  positive_15_to_20: number;
  positive_20_to_25: number;
  positive_25: number;
}

export interface IBillingPaymentScheduleApi {
  annual_due_date: string | null;
  q1_due_date: string | null;
  q2_due_date: string | null;
  q3_due_date: string | null;
  q4_due_date: string | null;
  type: BillingPaymentScheduleType;
}

export interface IBillingPaymentApi {
  payment_date: string | null;
  amount: string;
  period: BillingPaymentPeriod;
}

export interface IBillingRefundApi {
  id: number;
  amount: string;
  payment_date: string | null;
  reason: BillingRefundReason;
}

export interface IBillingApi {
  id: number;
  year: number;
  expected_waste_amount: number;
  waste_amount: number;
  expense_summary: number;
  submitted: boolean;
  balance: number;
  total_fee_sum: number;
  expected_expense_summary: number;
  household_member_sum: number;
  total_collected_fee_sum: string;
  avg_household_fee: string;
  avg_resident_fee: string;
  avg_household_fee_increase: string | null;
  avg_resident_fee_increase: string | null;
  household_count: number;
  household_with_fixed_only_count: number;
  status: BillingStatus;
  BillingExpenses: IBillingExpenseApi[];
  WeightedBillings?: IBillingWeightedApi;
  LocationBillingIncreaseComparison?: IBillingLocationIncreaseComparisonApi;
  PaymentSchedules?: IBillingPaymentScheduleApi[];
}

export interface IBillingExpense {
  id: string;
  apiId?: number;
  real: boolean;
  name: string;
  value: string;
}

export interface IBillingWeighted {
  rate: number;
  flatFee: number;
  suggestedFlatFee: number;
  suggestedRate: number;
  avgPublicWastePerPerson: number;
  lastYearsWasteSum: number;
  minWastePerHousehold: number;
  medianWastePerHousehold: number;
}

export interface IBillingLocationIncreaseComparison {
  negative25: number;
  negative20To25: number;
  negative15To20: number;
  negative10To15: number;
  negative5To10: number;
  negative0To5: number;
  positive0To5: number;
  positive5To10: number;
  positive10To15: number;
  positive15To20: number;
  positive20To25: number;
  positive25: number;
}

export interface IBillingPaymentSchedule {
  annualDueDate: string;
  q1DueDate: string;
  q2DueDate: string;
  q3DueDate: string;
  q4DueDate: string;
  type: BillingPaymentScheduleType;
}

export interface IBillingPayment {
  paymentDate: string | null;
  amount: number;
  period: BillingPaymentPeriod;
}

export interface IBillingRefund {
  readonly id: number;
  amount: number;
  paymentDate: string | null;
  reason: BillingRefundReason;
}

export interface IBilling {
  billingId: number;
  year: number;
  expectedWasteAmount: string;
  wasteAmount: string;
  expenseSummary: number;
  submitted: boolean;
  balance: number;
  totalFeeSum: number;
  expectedExpenseSummary: number;
  householdMemberSum: number;
  totalCollectedFeeSum: number;
  avgHouseholdFee: number;
  avgResidentFee: number;
  avgHouseholdFeeIncrease: number | null;
  avgResidentFeeIncrease: number | null;
  householdCount: number;
  householdWithFixedOnlyCount: number;
  status: BillingStatus;
  expenses: IBillingExpense[];
  weighted: IBillingWeighted;
  locationIncreaseComparison: IBillingLocationIncreaseComparison;
  paymentSchedule: IBillingPaymentSchedule;
  removeExpenseApiIds: number[];
  hasBeenSaved: boolean;
}

export interface IBillingPut {
  id: number;
  waste_amount?: number;
  expected_waste_amount?: number;
  expenses?: Partial<IBillingExpenseApi>[];
  weighted_rates?: {
    rate?: number;
    flat_fee?: number;
    min_waste_per_household?: number;
  };
  payment_schedule?: Partial<IBillingPaymentScheduleApi>;
}

export interface IBillingPutOptions {
  includeWasteAmount: boolean;
  includeExpectedWasteAmount: boolean;
  includeRealExpenses: boolean;
  includeNonRealExpenses: boolean;
  includeWeightedRates: boolean;
  includePaymentSchedule: boolean;
}

export interface IBillingsState {
  status: Status;
  requestId: string;
  errorMsg: string | null;
  list: IBilling[];
  isNextPageEnable: boolean;
  scrollTop: number;
  pagination: IPagination;
}
